import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Button, Badge, Modal } from '@tadagram/ui';
import { Plus, Edit, Trash2, Calendar, GitBranch, Play, Pause, ChevronDown, ChevronRight, ArrowRight } from 'lucide-react';
import { Campaign, CreateCampaignRequest } from '../types/campaign';
import { apiClient } from '../api/client';
import { CampaignForm } from '../components';

export const CampaignManagement: React.FC = () => {
  const { t } = useTranslation('campaign');
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<Campaign | undefined>(undefined);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [runningNowId, setRunningNowId] = useState<string | null>(null);
  const [togglingStatusId, setTogglingStatusId] = useState<string | null>(null);

  const loadCampaigns = useCallback(async () => {
    setIsLoading(true);
    try {
      const list = await apiClient.getCampaigns();
      setCampaigns(list);
    } catch (err) {
      console.error('Failed to load campaigns:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { loadCampaigns(); }, [loadCampaigns]);

  const handleSave = async (data: CreateCampaignRequest) => {
    if (editingCampaign) {
      await apiClient.updateCampaign(editingCampaign.id, data);
    } else {
      await apiClient.createCampaign(data);
    }
    setIsFormModalOpen(false);
    setEditingCampaign(undefined);
    loadCampaigns();
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(t('deleteConfirm'))) return;
    try {
      await apiClient.deleteCampaign(id);
      loadCampaigns();
    } catch (err: any) {
      alert(t('deleteError', { error: err.message }));
    }
  };

  const handleRunNow = async (id: string) => {
    try {
      setRunningNowId(id);
      await apiClient.runNowCampaign(id);
      window.alert(t('runNowSent'));
    } catch (err: any) {
      window.alert(t('runNowFailed', { error: err.message || 'Unknown error' }));
    } finally {
      setRunningNowId(null);
    }
  };

  const handleToggleStatus = async (campaign: Campaign) => {
    const shouldDeactivate = campaign.status === 'active';
    try {
      setTogglingStatusId(campaign.id);
      if (shouldDeactivate) {
        await apiClient.deactivateCampaign(campaign.id);
        window.alert(t('turnOffSent'));
      } else {
        await apiClient.activateCampaign(campaign.id);
        window.alert(t('turnOnSent'));
      }
      await loadCampaigns();
    } catch (err: any) {
      window.alert(t('toggleStatusFailed', { error: err.message || 'Unknown error' }));
    } finally {
      setTogglingStatusId(null);
    }
  };

  const toggleExpand = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const statusBadge = (status: string) => {
    const map: Record<string, { variant: any; label: string }> = {
      active:    { variant: 'success', label: t('statusActive') },
      draft:     { variant: 'neutral', label: t('statusDraft') },
      paused:    { variant: 'warning', label: t('statusPaused') },
      completed: { variant: 'primary', label: t('statusCompleted') },
    };
    const cfg = map[status] || { variant: 'neutral', label: status };
    return <Badge variant={cfg.variant}>{cfg.label}</Badge>;
  };

  return (
    <div className="h-full flex flex-col bg-tg-bg">
      {/* Page header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-tg-border bg-white">
        <div>
          <h1 className="text-xl font-bold text-tg-text">{t('pageTitle')}</h1>
          <p className="text-sm text-tg-hint mt-0.5">{t('pageSubtitle')}</p>
        </div>
        <Button
          size="md"
          icon={<Plus size={18} />}
          onClick={() => { setEditingCampaign(undefined); setIsFormModalOpen(true); }}
        >
          {t('createCampaign')}
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto p-6 space-y-4">
        {isLoading ? (
          <div className="text-center py-16">
            <div className="animate-spin h-8 w-8 border-4 border-tg-primary border-t-transparent rounded-full mx-auto mb-3" />
            <p className="text-tg-hint text-sm">{t('loading')}</p>
          </div>
        ) : campaigns.length === 0 ? (
          <Card>
            <div className="text-center py-16">
              <GitBranch className="mx-auto text-tg-hint mb-4" size={56} />
              <h3 className="text-lg font-semibold text-tg-text mb-2">{t('noCampaignsYet')}</h3>
              <p className="text-tg-hint text-sm max-w-sm mx-auto">{t('noCampaignsDesc')}</p>
              <Button
                className="mt-6"
                icon={<Plus size={16} />}
                onClick={() => { setEditingCampaign(undefined); setIsFormModalOpen(true); }}
              >
                {t('createCampaign')}
              </Button>
            </div>
          </Card>
        ) : (
          campaigns.map((campaign) => {
            const expanded = expandedIds.has(campaign.id);
            const wfOrder = campaign.workflow_order || [];
            const compiledWorkflows = campaign.compiled_workflows || [];
            return (
              <Card key={campaign.id}>
                {/* Campaign row */}
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => toggleExpand(campaign.id)}
                    className="p-1 rounded hover:bg-gray-100 text-tg-hint"
                  >
                    {expanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                  </button>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-tg-text">{campaign.name}</span>
                      {statusBadge(campaign.status)}
                      <span className="text-xs text-tg-hint">
                        {wfOrder.length} {t('workflowsCount', { count: wfOrder.length })}
                      </span>
                    </div>
                    {campaign.start_date && (
                      <div className="flex items-center gap-1.5 text-xs text-tg-hint mt-0.5">
                        <Calendar size={12} />
                        <span>
                          {t('from')} {new Date(campaign.start_date).toLocaleDateString('vi-VN')}
                          {campaign.end_date ? ` → ${new Date(campaign.end_date).toLocaleDateString('vi-VN')}` : ''}
                        </span>
                      </div>
                    )}
                    {campaign.description && (
                      <p className="text-xs text-tg-hint mt-0.5 truncate">{campaign.description}</p>
                    )}
                  </div>

                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => handleToggleStatus(campaign)}
                      className="p-2 text-amber-600 hover:bg-amber-50 rounded-lg disabled:opacity-60"
                      title={campaign.status === 'active' ? t('turnOff') : t('turnOn')}
                      disabled={togglingStatusId === campaign.id}
                    >
                      {campaign.status === 'active' ? <Pause size={16} /> : <Play size={16} />}
                    </button>
                    <button
                      onClick={() => handleRunNow(campaign.id)}
                      className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg disabled:opacity-60"
                      title={t('runNow')}
                      disabled={runningNowId === campaign.id || togglingStatusId === campaign.id}
                    >
                      <Play size={16} />
                    </button>
                    <button
                      onClick={() => { setEditingCampaign(campaign); setIsFormModalOpen(true); }}
                      className="p-2 text-tg-primary hover:bg-tg-primary/10 rounded-lg"
                      title={t('edit')}
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(campaign.id)}
                      className="p-2 text-tg-error hover:bg-tg-error/10 rounded-lg"
                      title={t('delete')}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>

                {/* Workflow sequence (expanded) */}
                {expanded && (
                  <div className="mt-4 pt-4 border-t border-tg-border">
                    {wfOrder.length === 0 ? (
                      <p className="text-sm text-tg-hint italic pl-2">{t('noWorkflowsLinked')}</p>
                    ) : (
                      <div className="flex flex-col gap-2 pl-2">
                        {[...wfOrder]
                          .sort((a, b) => a.order_index - b.order_index)
                          .map((entry, idx) => {
                            const compiledWf = compiledWorkflows.find(
                              (cw) => cw.workflow_id.endsWith('_' + entry.workflow_id)
                            );
                            const displayName =
                              compiledWf?.workflow_name || entry.workflow_name || entry.workflow_id;
                            return (
                            <div key={entry.workflow_id} className="flex items-center gap-2">
                              <div className="flex items-center gap-2 flex-1 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                                <span className="text-xs font-bold text-tg-hint w-5 shrink-0">
                                  {idx + 1}
                                </span>
                                <Play size={13} className="text-tg-primary shrink-0" />
                                <span className="text-sm font-medium text-tg-text flex-1 truncate">
                                  {displayName}
                                </span>
                                {(entry.delay_minutes ?? 0) > 0 && (
                                  <span className="text-xs text-tg-hint bg-white border border-gray-200 rounded px-1.5 py-0.5 shrink-0">
                                    +{entry.delay_minutes}m {t('delay')}
                                  </span>
                                )}
                              </div>
                              {idx < wfOrder.length - 1 && (
                                <ArrowRight size={14} className="text-tg-hint shrink-0" />
                              )}
                            </div>
                            );
                          })}
                      </div>
                    )}
                    <div className="mt-3 flex justify-end">
                      <Button
                        size="sm"
                        variant="ghost"
                        icon={<Edit size={14} />}
                        onClick={() => { setEditingCampaign(campaign); setIsFormModalOpen(true); }}
                      >
                        {t('editSchedule')}
                      </Button>
                    </div>
                  </div>
                )}
              </Card>
            );
          })
        )}
      </div>

      {/* Create / Edit modal */}
      <Modal
        isOpen={isFormModalOpen}
        onClose={() => {
          setIsFormModalOpen(false);
          setEditingCampaign(undefined);
          setFormError(null);
        }}
        title={editingCampaign ? t('editCampaign') : t('createNewCampaign')}
        size="xl"
        footer={
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              onClick={() => { setIsFormModalOpen(false); setEditingCampaign(undefined); }}
            >
              {t('cancel')}
            </Button>
            <Button
              variant="primary"
              loading={formLoading}
              onClick={() => {
                const form = document.getElementById('campaign-form') as HTMLFormElement;
                form?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
              }}
            >
              {editingCampaign ? t('updateCampaign') : t('createCampaign')}
            </Button>
          </div>
        }
      >
        <CampaignForm
          campaign={editingCampaign}
          onSave={handleSave}
          onCancel={() => { setIsFormModalOpen(false); setEditingCampaign(undefined); }}
          loading={formLoading}
          setLoading={setFormLoading}
          error={formError}
          setError={setFormError}
        />
      </Modal>
    </div>
  );
};
